import React, { useState } from "react";
import {
  FaGraduationCap, FaGlobe, FaBook, FaLinkedin,
  FaCertificate, FaChevronDown, FaMapMarkerAlt, FaCalendarAlt,
  FaPlayCircle,
} from "react-icons/fa";
import MediaEmbed from "./shared/MediaEmbed";
import "./Education.css";

// ─── Education data ───────────────────────────────────────────────────────
const EDUCATION = [
  {
    id: "erasmus",
    institution: "HU University of Applied Sciences Utrecht",
    degree: "Exchange Semester — Minor in International Communication (Erasmus+)",
    period: "Sep 2025 – Jan 2026",
    location: "Utrecht, Netherlands",
    status: "completed",
    flag: "🇳🇱",
    highlight: "Erasmus+",
    diplomaSlot: false,
    description: "Spent one semester at HU Utrecht through the Erasmus+ programme as part of a deliberate move to complement my technical background with international communication expertise. This experience fundamentally broadened my perspective on cross-cultural collaboration and communication strategy.",
    courses: [
      "Intercultural Communication",
      "Cultural Narrative & Storytelling",
      "Digital Media & Society",
      "Marketing Communication",
      "International Project Management",
    ],
    books: [
      { title: "Intercultural Communication", author: "Various Authors", coverImg: "/exchange-program/books/intercultural%20communciation.jpg" },
      { title: "Marketing Communications", author: "Various Authors", coverImg: "/exchange-program/books/mareting%20communications.jpg" },
      { title: "How Brands Grow", author: "Byron Sharp", coverImg: "/exchange-program/books/how%20brands%20grow.jpg" },
      { title: "Design Thinking", author: "Various Authors", coverImg: "/exchange-program/books/design%20thinking.jpg" },
    ],
    reflection: "Coming from a software engineering background, adapting to communication theory, qualitative research, and marketing frameworks was a genuine challenge — and one of the most rewarding experiences of my career. I learned that technology without cultural empathy is incomplete. This semester shaped how I approach product design, team communication, and cross-cultural stakeholder work.",
    certsEarned: ["IAA Accredited Course Completion", "EACA Accreditation in Commercial Communications"],
    linkedinPost: "https://www.linkedin.com/feed/update/urn:li:activity:7429271185344860160/",
    imageGallerySlots: 0,
    photos: [
      "/exchange-program/WhatsApp%20Image%202026-01-21%20at%201.06.13%20PM%20(1).jpeg",
      "/exchange-program/WhatsApp%20Image%202026-01-21%20at%201.07.03%20PM%20(1).jpeg",
      "/exchange-program/WhatsApp%20Image%202026-01-21%20at%201.07.30%20PM%20(1).jpeg",
      "/exchange-program/WhatsApp%20Image%202026-01-21%20at%201.09.53%20PM%20(1).jpeg",
      "/exchange-program/WhatsApp%20Image%202026-01-21%20at%201.11.12%20PM%20(1).jpeg",
      "/exchange-program/WhatsApp%20Image%202026-01-21%20at%201.12.12%20PM%20(1).jpeg",
      "/exchange-program/WhatsApp%20Image%202026-01-21%20at%201.12.13%20PM%20(8).jpeg",
      "/exchange-program/WhatsApp%20Image%202026-01-21%20at%201.12.13%20PM%20(9).jpeg",
      "/exchange-program/WhatsApp%20Image%202026-01-21%20at%201.12.13%20PM%20(10).jpeg",
      "/exchange-program/WhatsApp%20Image%202026-01-21%20at%201.12.13%20PM%20(11).jpeg",
      "/exchange-program/WhatsApp%20Image%202026-01-21%20at%201.12.13%20PM%20(12).jpeg",
      "/exchange-program/WhatsApp%20Image%202026-01-21%20at%201.12.13%20PM%20(13).jpeg",
    ],
    presentations: [
      {
        type: "video",
        src: "/exchange-program/Final%20Assessment%20Presentation.mp4",
        label: "Final Assessment — International Communication",
        contextNote: "My final reflection for the International Communication Minor at HU Utrecht. Five months of working in international teams reshaped how I think about audience, strategy, and what communication actually means.",
      },
      {
        type: "video",
        src: "/exchange-program/Learning%20Video%20Editing.mp4",
        label: "Learning Video Editing as a Creative Skill",
        contextNote: "How I deliberately built video editing (CapCut) as a professional communication tool during my exchange — from academic assignments to personal vlogs of the Netherlands.",
      },
      {
        type: "video",
        src: "/exchange-program/The%20end%20presentatie%20HEINZ%20.mp4",
        label: "Group Project — HEINZ Final Presentation",
        contextNote: "End-of-semester group project presentation. Cross-cultural team, international creative brief.",
        lang: "nl",
      },
      {
        type: "pdf",
        src: "/exchange-program/Cultural%20Map%20assignment%20%20Tunisia%20.pdf",
        label: "Cultural Map — Tunisia × Netherlands",
        contextNote: "Cross-cultural analysis of Tunisian and Dutch business culture using Hofstede, Hall, and Erin Meyer frameworks. Covers communication style, time orientation, decision-making hierarchy, and trust-building.",
        lang: "fr",
      },
      {
        type: "pdf",
        src: "/exchange-program/Virtual%20Reality%20Presentation.pdf",
        label: "VR & AR in Marketing",
        contextNote: "Research-based presentation on immersive technology adoption in marketing. Market growing from $42.4B (2023) to $826.6B by 2032. Case studies: IKEA, Sephora, Volvo, Burger King.",
      },
    ],
  },
  {
    id: "master",
    institution: "Faculty of Economic Sciences and Management (FSEGT), University of Tunis El Manar",
    degree: "Master's Degree — Information Systems Engineering & Data Science",
    period: "Sep 2024 – Present",
    location: "Tunis, Tunisia",
    status: "in_progress",
    flag: "🇹🇳",
    highlight: "Data Science",
    logo: "/logos/fsegt.png",
    diplomaSlot: true,
    diplomaImg: null,
    description: "Graduate programme combining information systems engineering with applied data science. Deepened expertise in machine learning, predictive modelling, Big Data engineering, and enterprise system architecture.",
    courses: [
      "Machine Learning & Predictive Modelling",
      "Big Data Engineering (Spark, Hadoop)",
      "Information Systems Architecture",
      "Advanced Databases & Data Warehousing",
      "Research Methodology",
    ],
    books: [],
    reflection: "This Master's programme gave me the rigorous theoretical foundation to complement my practical experience — covering everything from distributed computing with Spark to advanced ML pipelines and enterprise system design.",
    certsEarned: ["Machine Learning and AI with Python (Harvard)", "Data Science: Building ML Models (Harvard)"],
    linkedinPost: null,
    imageGallerySlots: 0,
  },
  {
    id: "bachelor",
    institution: "Higher Institute of Information and Communication Technologies (ISTIC), University of Carthage",
    degree: "Bachelor's Degree — Computer Science, Software Engineering",
    period: "Sep 2021 – May 2024",
    location: "Tunis, Tunisia",
    status: "completed",
    flag: "🇹🇳",
    highlight: "Software Engineering",
    logo: "/logos/logo_istic.jpg",
    diplomaSlot: true,
    diplomaImg: "/trainings/bachelor diploma.jpeg",
    description: "3-year undergraduate programme covering the full computer science and software engineering curriculum. Built foundations in web, mobile, backend, databases, algorithms, and project management — directly informing 5 internships and a startup.",
    courses: [
      "Web Development (Angular, React, HTML/CSS)",
      "Mobile Development (Flutter, React Native)",
      "Backend (Spring Boot, Jakarta EE, Node.js)",
      "Databases (SQL, PostgreSQL, MongoDB)",
      "Algorithms & Data Structures",
      "Software Engineering & Scrum",
      "Computer Vision & ML",
    ],
    books: [],
    reflection: "My Bachelor's was the foundation of everything. From building my first Angular app during an internship at CNI, to co-founding PathFinder, to competing in Hult Prize — it was 3 years of building, failing, and shipping real things.",
    certsEarned: ["Software Engineering (Saylor University)", "Machine Learning with Python (freeCodeCamp)", "Data Analysis with Python (freeCodeCamp)"],
    linkedinPost: null,
    imageGallerySlots: 0,
  },
];

// ─── DiplomaSlot ──────────────────────────────────────────────────────────
const DiplomaSlot = ({ status, diplomaImg }) => {
  if (diplomaImg) {
    return (
      <div className="edu-diploma-slot">
        <img
          src={diplomaImg}
          alt="Diploma"
          className="edu-diploma-img"
          onError={(e) => { e.target.style.display = "none"; }}
        />
      </div>
    );
  }
  return (
    <div className="edu-diploma-slot">
      <span>🎓</span>
      <span>
        {status === "completed"
          ? "Diploma image coming soon"
          : "In progress — diploma will appear here upon completion"}
      </span>
    </div>
  );
};

// ─── EducationCard ────────────────────────────────────────────────────────
const EducationCard = ({ item }) => {
  const [open, setOpen] = useState(item.id === "erasmus");

  return (
    <div className={`edu-card ${open ? "edu-card--open" : ""}`}>
      {/* Header */}
      <button className="edu-card__header" onClick={() => setOpen(!open)}>
        <div className="edu-card__header-left">
          {item.logo ? (
            <img src={item.logo} alt={item.institution} className="edu-card__logo" onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <span className="edu-card__flag">{item.flag}</span>
          )}
          <div>
            <div className="edu-card__degree">{item.degree}</div>
            <div className="edu-card__institution">{item.institution}</div>
            <div className="edu-card__meta">
              <span><FaCalendarAlt /> {item.period}</span>
              <span><FaMapMarkerAlt /> {item.location}</span>
              <span className={`edu-status-badge edu-status-badge--${item.status}`}>
                {item.status === "completed" ? "✓ Completed" : "In Progress"}
              </span>
            </div>
          </div>
        </div>
        <span className="edu-card__chevron">
          <FaChevronDown />
        </span>
      </button>

      {/* Body */}
      {open && (
        <div className="edu-card__body">
          {item.highlight === "Erasmus+" && (
            <div className="edu-erasmus-banner">
              <FaGlobe /> Erasmus+ Exchange Programme
            </div>
          )}

          <p className="edu-description">{item.description}</p>

          {/* Courses */}
          <div className="edu-section">
            <div className="edu-section__label"><FaBook /> Subjects Studied</div>
            <div className="edu-tags">
              {item.courses.map((c, i) => (
                <span key={i} className="edu-tag">{c}</span>
              ))}
            </div>
          </div>

          {/* Books */}
          {item.books.length > 0 && (
            <div className="edu-section">
              <div className="edu-section__label">📚 Books Studied</div>
              <div className="edu-books">
                {item.books.map((b, i) => (
                  <div key={i} className="edu-book-card">
                    {b.coverImg ? (
                      <img
                        src={b.coverImg}
                        alt={b.title}
                        className="edu-book-cover-img"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <div className="edu-book-cover">📖</div>
                    )}
                    <div>
                      <div className="edu-book-title">{b.title}</div>
                      <div className="edu-book-author">{b.author}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reflection */}
          <div className="edu-section">
            <div className="edu-section__label">💭 Reflection</div>
            <blockquote className="edu-reflection">{item.reflection}</blockquote>
          </div>

          {/* Certs earned */}
          {item.certsEarned.length > 0 && (
            <div className="edu-section">
              <div className="edu-section__label"><FaCertificate /> Certificates Earned During This Period</div>
              <div className="edu-tags">
                {item.certsEarned.map((c, i) => (
                  <span key={i} className="edu-tag edu-tag--cert">{c}</span>
                ))}
              </div>
            </div>
          )}

          {/* Presentations & Videos */}
          {item.presentations && item.presentations.length > 0 && (
            <div className="edu-section">
              <div className="edu-section__label"><FaPlayCircle /> Presentations &amp; Work Samples</div>
              <div className="edu-presentations">
                {item.presentations.map((p, i) => (
                  <MediaEmbed
                    key={i}
                    src={p.src}
                    type={p.type}
                    label={p.label}
                    contextNote={p.contextNote}
                    lang={p.lang}
                  />
                ))}
              </div>
            </div>
          )}

          {/* LinkedIn post (Erasmus) */}
          {item.linkedinPost && (
            <div className="edu-section">
              <div className="edu-section__label"><FaLinkedin /> My Erasmus Experience Post</div>
              <a
                href={item.linkedinPost}
                target="_blank"
                rel="noopener noreferrer"
                className="edu-linkedin-btn"
              >
                <FaLinkedin /> View my LinkedIn post about Utrecht →
              </a>
            </div>
          )}

          {/* Diploma */}
          {item.diplomaSlot && <DiplomaSlot status={item.status} diplomaImg={item.diplomaImg} />}
        </div>
      )}
    </div>
  );
};

// ─── Education ────────────────────────────────────────────────────────────
const Education = () => (
  <div className="education-section">
    <h2 className="section-title">Education</h2>
    <p className="edu-intro">
      3 institutions across Tunisia and the Netherlands — software engineering foundations,
      a Data Science Master's, and an Erasmus+ semester in International Communication.
    </p>
    {EDUCATION.map((item) => (
      <EducationCard key={item.id} item={item} />
    ))}
  </div>
);

export default Education;
